import { action, computed, observable, makeObservable } from 'mobx';
import { SecureUser, V3MembershipType } from '../types/user';
import { UserApis } from '../api';
import { TokenListState } from '../../mainv3/types/tokens';
import { AvailableTokenApis } from '../../mainv3/apis/tokens';
import { SubmitState } from '../../commonv3/states/SubmitState';
import { UserPreferenceState } from './UserPreferenceState';
import { PaymentApis } from '../../mainv3/apis/payments';
import { PaymentIndexView } from '../../mainv3/types/payments';
import { updateRemoteResourceV3, RemoteResourceV3 } from '../../commonv3/api/RemoteResourceV3';
import moment, { unitOfTime } from 'moment-timezone';
import { HomeLocationState } from '../../commonv3/states/HomeLocationState';
import { WithdrawalState } from '../../commonv3/states/WithdrawalsState';
import autobind from 'autobind-decorator';
import { MembershipPlanGroupKey } from '../../mainv3/types/membershipPlans';
import { Service } from '../../commonv3/types/service';

declare const initialAppState: {
  currentUser: SecureUser | null;
  enabledFlagKeys: string[];
  activeVariantKeys: string[];
};

export function diffInMonths(dateFrom: string, dateTo: string = null): number {
  const from = moment(dateFrom);
  const to = dateTo ? moment(dateTo) : moment();
  return to.diff(from, 'month');
}

export const FlagKeys = {
  COMMUNITY_SERVICE: 'community_service',
  show_account_only_selector: 'show_account_only_selector',
  SHOW_INTRODUCTION_OF_1on1_COACHING: 'show_introduction_of_1on1_coaching',
  SHOW_EVENT_AREA_IN_HOME_PAGE: 'show_event_area_in_home_page',
  SHOW_NEW_KARTE_BANNER_ON_TRIAL_LESSON_COMPLETION_PAGE: 'show_new_karte_banner_on_trial_lesson_completion_page',
  SHOW_CAROUSEL_IN_EVENT_PAGE: 'show_carousel_in_event_page',
  SHOW_ONLY_WELCOME_WORKSHOP_LABEL: 'show_only_welcome_workshop_label',
  /** SHE-4987: イベントページの遷移先をオーバーレイから個別ページに切り替えるフラグ */
  REDIRECT_EVENT_DETAIL_PAGE: 'redirect_event_detail_page',
  /** INNOVATIVE WOMEN'S SUMMIT のみ応募者数の表示をフラグ制御にする */
  // https://sheinc.atlassian.net/browse/SHE-5091
  HIDE_RESERVATION_COUNT_FOR_IWS: 'hide_reservation_count_for_iws',
  // CS監査対応ph1用のフラグ
  ADMIN_CS_KANSA_PHASE_1: 'admin_cs_kansa_phase_1',
  // CS監査対応ph2用のフラグ
  ADMIN_CS_KANSA_PHASE_2: 'admin_cs_kansa_phase_2',
  // CS監査対応ph3:返金方法の変更
  ADMIN_CS_KANSA_PHASE_3_UPDATE_REFUND_METHOD: 'admin_cs_kansa_phase_3_update_refund_method',
  // CS監査対応ph3: 事後クーポン付与
  ADMIN_CS_KANSA_PHASE_3_UPDATE_ORDER_BY_COUPON: 'admin_cs_kansa_phase_3_update_order_by_coupon',
  // IWSのバナーをホーム画面に表示する用のフラグ
  SHOW_IWS_BANNER: 'show_iws_banner',
};

export class CurrentUserState {
  loadState: SubmitState = new SubmitState();
  currentUser: SecureUser = initialAppState.currentUser;
  availableTokens: TokenListState = new TokenListState();
  homeLocation: HomeLocationState = new HomeLocationState();
  withdrawal: WithdrawalState = new WithdrawalState();
  payments: RemoteResourceV3<PaymentIndexView[]> = RemoteResourceV3.loading();

  constructor() {
    makeObservable(this, {
      loadState: observable,
      currentUser: observable,
      availableTokens: observable,
      homeLocation: observable,
      withdrawal: observable,
      payments: observable,
      reloadUser: action,
      preference: computed,
      enabledFlagKeys: computed,
      activeVariantKeys: computed,
      loading: computed,
      userId: computed,
      failedTransactions: computed,
      hasFailedTransactions: computed,
      firstPaymentDateAfterMigration: computed,
      isDeactivated: computed,
      updateCurrentUser: action,
    });
  }

  getOrThrow() {
    if (!this.currentUser) {
      throw new Error('User must be signed in');
    }
  }

  /**
   * Look up value for the current user.
   * Intended for reliable look up of user values regardless whether the user is signed in or not
   * @param key the key to look up (field of SecureUser)
   * @param defaultValue the default value
   */
  @autobind
  getValue<K extends keyof SecureUser>(key: keyof SecureUser, defaultValue: SecureUser[K]): SecureUser[K] {
    if (!this.currentUser) {
      return defaultValue;
    }
    return this.currentUser[key];
  }

  async reloadUser(): Promise<SecureUser> {
    await this.loadState.operate(async () => {
      this.currentUser = await UserApis.getCurrentUser();
      if (this.currentUser) {
        await Promise.all([
          await updateRemoteResourceV3(
            this.payments,
            (v) => (this.payments = v),
            () => PaymentApis.findAll([Service.LIKES])
          ),
          this.availableTokens.init(await AvailableTokenApis.findAll(Service.LIKES)),
          this.homeLocation.init(),
          this.withdrawal.init(),
        ]);
      } else {
        this.availableTokens.init([]);
      }
    });
    return this.currentUser;
  }

  get preference(): UserPreferenceState {
    if (!this.currentUser) {
      return new UserPreferenceState({});
    }
    return new UserPreferenceState(this.currentUser.user_preference);
  }

  /**
   * このユーザに対して現在有効なFlagのキーの一覧を返す
   */
  get enabledFlagKeys(): string[] {
    if (!initialAppState.enabledFlagKeys) {
      return [];
    }
    return initialAppState.enabledFlagKeys;
  }

  get activeVariantKeys(): string[] {
    return initialAppState.activeVariantKeys;
  }

  hasEnabledFlagKey(key: string): boolean {
    return this.enabledFlagKeys.includes(key);
  }

  hasActiveVariant(flagKey: string, value: string) {
    return this.activeVariantKeys.includes(`${flagKey}-${value}`);
  }

  get loading(): boolean {
    return this.loadState.submitting;
  }

  get userId() {
    return this.currentUser ? this.currentUser.id : null;
  }

  isSignedIn(): boolean {
    return this.currentUser != null;
  }

  isMember(): boolean {
    return this.isSignedIn() && this.currentUser.v2_member;
  }

  isV3Member(): boolean {
    return this.isSignedIn() && this.currentUser.v3_member;
  }

  isMultiCreatorUser(): boolean {
    return (
      this.isSignedIn() && this.currentUser.membership_plan_group_keys.includes(MembershipPlanGroupKey.MULTICREATOR)
    );
  }

  isEffectiveMultiCreatorUser(): boolean {
    return this.isSignedIn() && this.currentUser.has_effective_multicretor_membership_token;
  }

  hasEffectiveMembershipToken(): boolean {
    return this.isSignedIn() && this.currentUser.has_effective_membership_token;
  }

  isV3StandardMember(): boolean {
    return this.isSignedIn() && this.currentUser.v3_membership_type == V3MembershipType.STANDARD;
  }

  isV3TrialMember(): boolean {
    return this.isSignedIn() && this.currentUser.v3_membership_type == V3MembershipType.TRIAL;
  }

  isNonMember(): boolean {
    return !this.isMember();
  }

  hasCard(): boolean {
    return this.isSignedIn() && !!this.currentUser.payment_info;
  }

  hasLikesActiveSubscription(): boolean {
    return this.isSignedIn() && this.currentUser.has_likes_active_subscription;
  }

  get failedTransactions(): PaymentIndexView[] {
    return this.payments.getOrElse([]).filter((p) => p.status === 'error');
  }

  get hasFailedTransactions() {
    return this.failedTransactions.length > 0;
  }

  get firstPaymentDateAfterMigration() {
    if (!this.currentUser) {
      return null;
    }
    return this.currentUser.first_payment_date_after_migration;
  }

  isFirstPaymentAfterMigrationWithin(duration: number, unit: unitOfTime.DurationConstructor) {
    if (!this.firstPaymentDateAfterMigration) {
      return false;
    }
    const firstPaymentDate = moment(this.firstPaymentDateAfterMigration);
    return firstPaymentDate.isAfter(moment()) && moment().add(duration, unit).isSameOrAfter(firstPaymentDate);
  }

  get isDeactivated(): boolean {
    return this.failedTransactions.find((p) => diffInMonths(p.due_date) >= 1) != null;
  }

  /**
   * テストのためのメソッド。
   * テストの実行時に`initialAppState.currentUser`をモックすることが難しいため、このメソッドを使用する。
   *
   * FIXME: `initialAppState.currentUser`をモックしやすいように`GlobalStatesV3`などの構造を修正する。
   */
  updateCurrentUser(currentUser: Partial<SecureUser> | null) {
    this.currentUser = { ...this.currentUser, ...currentUser };
  }
}
